import { z } from 'zod';
import type { Permission } from '../permissions';

/**
 * What an id field on a response points at (ADR 0061). The kind — not the field
 * name — is what an expander resolves by, so `createdByActorId`,
 * `formerResponsibleMemberId` and `responsibleMemberIds` all resolve as members
 * whatever they are called.
 */
export const REFERENCE_KINDS = [
  'member',
  'contact',
  'property',
  'organization',
  'document',
  'api-key',
  'company',
  'lead',
  'lead-partner',
  'deal',
  'prospect',
  'valuation-request',
] as const;

export type ReferenceKind = (typeof REFERENCE_KINDS)[number];

/**
 * A dedicated registry rather than `.meta()`: `.meta()` writes to zod's global
 * registry, which `z.toJSONSchema` emits into every published OpenAPI and MCP
 * output schema. The tag is server-side wiring, not contract surface.
 */
export const referenceRegistry = z.registry<
  { readonly kind: ReferenceKind; readonly sibling?: string } | { readonly by: string; readonly sibling?: string }
>();

/**
 * Tag an id schema with the entity it references. Wrap the innermost id schema —
 * `refersTo('member', z.uuid()).nullable()` — so the tag survives `.nullable()`,
 * `.optional()`, `z.array()`, `.pick()` and `.extend()` alike.
 *
 * `sibling` overrides the derived sibling name for a field whose derived name the
 * contract already declares: the override is the derived name plus `Ref`
 * (`property` → `propertyRef`).
 */
export function refersTo<T extends z.ZodType>(
  kind: ReferenceKind,
  schema: T,
  options?: { readonly sibling?: string },
): T {
  referenceRegistry.add(schema, { kind, ...(options?.sibling !== undefined ? { sibling: options.sibling } : {}) });

  return schema;
}

/** The kind each `subjectType` token names, for ids whose kind a sibling enum field carries. */
export const SUBJECT_REFERENCE_KINDS = {
  DEAL: 'deal',
  LEAD: 'lead',
  PROPERTY: 'property',
  CONTACT: 'contact',
  PROSPECT: 'prospect',
  VALUATION_REQUEST: 'valuation-request',
} as const satisfies Readonly<Record<string, ReferenceKind>>;

/** Tag an id whose kind is named by a sibling enum field of the same object. */
export function refersToSubject<T extends z.ZodType>(
  discriminator: string,
  schema: T,
  options?: { readonly sibling?: string },
): T {
  referenceRegistry.add(schema, {
    by: discriminator,
    ...(options?.sibling !== undefined ? { sibling: options.sibling } : {}),
  });

  return schema;
}

/** The slim summary an expanded ref carries: enough to name the record, never the record itself. */
export const ReferenceSummarySchema = z.object({ id: z.string(), label: z.string() });

export type ReferenceSummary = z.infer<typeof ReferenceSummarySchema>;

/**
 * The grants that let a caller see a kind's label, any-of — the kind's own read
 * grant on the workspace surface, so an expansion never shows what the caller
 * could not have fetched with one more call. Workspace tier throughout: the
 * audience's permissions are resolved at the member's own Standort and applied to
 * records of every visible Standort, which is only sound for grants that do not
 * vary per Standort (BL-029, BL-091). An `organization:` literal is allowed only
 * when every org role in the catalogue carries it, so it cannot vary per Standort.
 * `document`, `lead-partner` and `valuation-request` are the cases, each pinned by
 * `static-org-role-catalogue.test.ts`. A
 * second literal exists only for a member-implicit read a key cannot be granted.
 */
export const REFERENCE_READ_PERMISSIONS: Readonly<Record<ReferenceKind, readonly [Permission, ...Permission[]]>> = {
  member: ['workspace:identity:member-read'],
  contact: ['workspace:relationship:contact-read'],
  property: ['workspace:property:property-read'],
  organization: ['workspace:identity:organization-read', 'workspace:identity:organizations-read'],
  document: ['organization:property:document-read', 'workspace:property:document-read'],
  'api-key': ['workspace:identity:api-key-read'],
  company: ['workspace:relationship:company-read'],
  lead: ['workspace:lead:lead-read'],
  'lead-partner': ['organization:lead:lead-partner-read'],
  deal: ['workspace:deal:deal-read'],
  prospect: ['workspace:deal:prospect-read'],
  'valuation-request': ['organization:valuation:valuation-request-read'],
};

/**
 * The operator's grants per kind (IMM-1120.5): a backoffice literal is not Standort-scoped, so one credential set
 * answers for every row of every workspace (BL-091).
 */
export const BACKOFFICE_REFERENCE_READ_PERMISSIONS: Readonly<Record<ReferenceKind, ReadonlyArray<Permission>>> = {
  member: ['backoffice:identity:member-read'],
  contact: ['backoffice:relationship:contact-read'],
  property: ['backoffice:property:property-read'],
  organization: ['backoffice:identity:organization-read'],
  document: ['backoffice:property:property-read'], // a document is read through its Objekt (admin document read: `contracts.ts:4290-4300`)
  'api-key': ['backoffice:identity:api-key-read'],
  company: ['backoffice:relationship:company-read'],
  lead: ['backoffice:lead:lead-read'],
  'lead-partner': ['backoffice:lead:lead-read'], // partner attribution is part of the Lead the operator reads
  deal: ['backoffice:deal:deal-read'],
  prospect: ['backoffice:deal:prospect-read'],
  'valuation-request': ['backoffice:valuation:valuation-request-read'],
};

/** English docs nouns for each kind, used in the published sibling descriptions. */
export const REFERENCE_KIND_NOUNS: Readonly<Record<ReferenceKind, { readonly one: string; readonly many: string }>> = {
  member: { one: 'member', many: 'members' },
  contact: { one: 'Kontakt (contact)', many: 'Kontakte (contacts)' },
  property: { one: 'Objekt (property)', many: 'Objekte (properties)' },
  organization: { one: 'Standort (location)', many: 'Standorte (locations)' },
  document: { one: 'Objekt document', many: 'Objekt documents' },
  'api-key': { one: 'API key', many: 'API keys' },
  company: { one: 'Firma (company)', many: 'Firmen (companies)' },
  lead: { one: 'Lead', many: 'Leads' },
  'lead-partner': { one: 'lead partner', many: 'lead partners' },
  deal: { one: 'deal', many: 'deals' },
  prospect: { one: 'Interessent (prospect)', many: 'Interessenten (prospects)' },
  'valuation-request': { one: 'Bewertung (valuation request)', many: 'Bewertungen (valuation requests)' },
};
